import * as moment from 'moment';
import React, { useState } from 'react';
import { IStrongBoxCenterFilter } from '@nkr/models/strong-box/strong-box-filter';
import { Button, ControlLabel, Form, FormGroup, Well } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import { IStrongBoxOpoFilter } from '@nkr/models/strong-box/strong-box-filter';
import { useAppHookContext } from '@nkr/utils/rest-api';
import Legend, { TLegendEntry } from './legend';
import CenterDropDown from '@nkr/components/shared/center-drop-down';
import OpoDropDown from '@nkr/components/shared/opo-drop-down';

type TStrongBoxFilter = IStrongBoxCenterFilter | IStrongBoxOpoFilter;
type ChangeHandler = (f: TStrongBoxFilter) => void;

type IProps<T extends TStrongBoxFilter> = {
    filter: T,
    defaultFilter: T,
    startDateLabel?: string,
    endDateLabel?: string,
    legendEntries?: TLegendEntry[],
    isCenter: boolean,
    onChange: ChangeHandler,
    onExport?: () => void,
    onAssignCenters?: () => void
}

const StrongBoxFilter = <T extends IStrongBoxCenterFilter | IStrongBoxOpoFilter>({ filter, defaultFilter, startDateLabel, endDateLabel, legendEntries, isCenter, onChange, onExport, onAssignCenters }: IProps<T>) => {
    const { app: { user } } = useAppHookContext();

    const [centerId, setCenterId] = useState<string>('');
    const [opoId, setOpoId] = useState<string>('');
    const [startDate, setStartDate] = useState<string>(Utility.getDateInputStr(filter.startDate));
    const [endDate, setEndDate] = useState<string>(Utility.getDateInputStr(filter.endDate));

    let startDateGroup: JSX.Element = <></>;

    if (startDateLabel) {
        startDateGroup = (
            <>
                <FormGroup>
                    <ControlLabel>{startDateLabel}</ControlLabel>{' '}
                    <input type='date' className='form-control' value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </FormGroup>&nbsp;&nbsp;
            </>
        );
    }

    let endDateGroup: JSX.Element = <></>;

    if (endDateLabel) {
        endDateGroup = (
            <>
                <FormGroup>
                    <ControlLabel>{endDateLabel}</ControlLabel>{' '}
                    <input type='date' className='form-control' value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </FormGroup>&nbsp;&nbsp;
            </>
        );
    }

    const update = () => {
        if (isCenter) {
            onChange({
                centerId: centerId ? parseInt(centerId) : null,
                startDate: startDate ? moment(startDate).toDate() : null,
                endDate: endDate ? moment(endDate).toDate() : null
            });
        } else {
            onChange({
                opoId: opoId ? parseInt(opoId) : null,
                startDate: startDate ? moment(startDate).toDate() : null,
                endDate: endDate ? moment(endDate).toDate() : null,
                isCenter: false
            });
        }
    }

    const handleReset = () => {
        if (isCenter) {
            const f: IStrongBoxCenterFilter = defaultFilter;
            setCenterId(f.centerId ? f.centerId.toString() : '');
        } else {
            const f: IStrongBoxOpoFilter = defaultFilter;
            setOpoId(f.opoId ? f.opoId.toString() : '');
        }

        setStartDate(Utility.getDateInputStr(defaultFilter.startDate));
        setEndDate(Utility.getDateInputStr(defaultFilter.endDate));
        onChange(defaultFilter);
    }

    return (
        <Well bsSize='sm' style={{ marginBottom: 5 }}>
            <Form inline>
                {!user.opoId && (
                    <>
                        <FormGroup>
                            <ControlLabel>{isCenter ? 'Center' : 'OPO'}:</ControlLabel>&nbsp;&nbsp;
                            {isCenter && (
                                <CenterDropDown className='form-control' value={centerId} onChange={setCenterId} unselectedText='-ALL-' />
                            )}

                            {!isCenter && (
                                <OpoDropDown className='form-control' value={opoId} unselectedText='-ALL-' includeCenters={false} onChange={setOpoId} />
                            )}
                        </FormGroup>&nbsp;&nbsp;
                    </>
                )}
                {startDateGroup}
                {endDateGroup}
                <Button bsStyle='primary' type='button' onClick={update}>Search</Button>{' '}
                <Button type='button' onClick={handleReset}>Reset</Button>
                {legendEntries && <Legend entries={legendEntries} />}
                <div className='pull-right' style={{ display: 'flex' }}>
                    {!user.opoId && !isCenter && (
                        <><Button className='pull-right' onClick={onAssignCenters}>Assign Centers to OPO</Button>&nbsp;&nbsp;</>
                    )}
                    {onExport && <Button type='button' bsStyle='success' onClick={onExport}>Export (CSV)</Button>}
                </div>
            </Form>
        </Well>
    );
}

export default StrongBoxFilter;